import { LessonsApi, PrintQuota } from 'api/lessonsApi';
import SubjectSitePopup from 'components/subjectSite/SubjectSitePopup';
import { onEnterOrSpace } from 'components/subjectSite/subjectSiteMenu';
import ProgressImage from 'images/subjectSite/progress-indicator-6.gif';
import React, { useEffect, useRef, useState } from 'react';

const PRINT_VIA_BUTTON_CLASS = 'LessonPrintViaButton';
// At this width and below any print shows the page, as phones and tablets have no Print button
// shortcut to steer away from.
const PRINT_WARNING_MIN_WIDTH = 1024;

type HeldAction = {
  quota: PrintQuota;
  action: () => void;
};

/**
 * Counts prints and knowledge organiser opens against the school's monthly print limit.
 * A warning from the API shows in a popup first; past the limit nothing prints or opens.
 */
export const useLessonPrint = (subjectsId: number | null) => {
  const [held, setHeld] = useState<HeldAction | null>(null);
  const [bError, setBError] = useState(false);
  const bViaButton = useRef(false);

  useEffect(() => {
    const beforePrint = () => {
      if (bViaButton.current || window.innerWidth <= PRINT_WARNING_MIN_WIDTH) {
        document.body.classList.add(PRINT_VIA_BUTTON_CLASS);
      }
    };
    const afterPrint = () => {
      bViaButton.current = false;
      document.body.classList.remove(PRINT_VIA_BUTTON_CLASS);
    };
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
      afterPrint();
    };
  }, []);

  const printPage = () => {
    bViaButton.current = true;
    document.body.classList.add(PRINT_VIA_BUTTON_CLASS);
    window.print();
  };

  /**
   * Checks the quota, then runs the action unless the API warns first.
   * @param onHeld - Runs when the action does not go ahead at once
   */
  const checkQuota = async (
    printPageUrl: string,
    action: () => void,
    onHeld?: () => void
  ) => {
    if (subjectsId === null) return;
    setBError(false);
    const quota = await LessonsApi.checkPrintQuota(subjectsId, printPageUrl);
    if (!quota) {
      onHeld?.();
      setBError(true);
    } else if (quota.warningMessage) {
      onHeld?.();
      setHeld({ quota, action });
    } else {
      action();
    }
  };

  const print = () => checkQuota(window.location.pathname, printPage);

  const closePopup = () => setHeld(null);
  const continuePopup = () => {
    const action = held?.action;
    setHeld(null);
    action?.();
  };

  // The popup keeps its last message while it fades out
  const shownQuota = useRef<PrintQuota | null>(null);
  if (held) shownQuota.current = held.quota;
  const bExceeded = shownQuota.current?.bPrintQuotaExceeded ?? false;
  const popupButton = held?.quota.bPrintQuotaExceeded
    ? closePopup
    : continuePopup;

  const popup = (
    <SubjectSitePopup
      variant="PopupParentV2"
      isOpen={held !== null}
      onClose={closePopup}
      width={600}
    >
      <div className="PopupMsg NoSpacing">
        <div
          className="CloseBtn3"
          role="button"
          aria-label="Close Pop-up Here"
          tabIndex={0}
          onClick={closePopup}
          onKeyDown={onEnterOrSpace(closePopup)}
        ></div>
        <h2 className="Hdr3">Print Limit Quota</h2>
      </div>
      <div className="ProgressIndicator Hide">
        <img src={ProgressImage} alt="Loading" />
      </div>
      <div className="PopupMsg MiddleBlock LeftAlign">
        <div className="MsgContainer">
          <div className="ErrorMsg"></div>
          <p
            dangerouslySetInnerHTML={{
              __html: shownQuota.current?.warningMessage ?? '',
            }}
          />
        </div>
      </div>
      <div className="BtmBtnsContainer">
        <div className="BtmBtns">
          <div className="RightBtn BtnOne">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              tabIndex={1}
              onClick={(event) => {
                event.preventDefault();
                popupButton();
              }}
            >
              {bExceeded ? 'Close' : 'Continue'}
            </a>
          </div>
        </div>
      </div>
    </SubjectSitePopup>
  );

  return { print, checkQuota, popup, bError };
};

type PartsProps = {
  schoolName?: string | null;
};

/**
 * The line a print from outside the Print button shows instead of the page, and the printout footer
 */
export const LessonPrintParts: React.FC<PartsProps> = ({ schoolName }) => (
  <>
    <p className="LessonPrintWarning">
      If you want to print this resource, please use the blue "Print" button on
      your screen. If you need any help using your platform please reach out to
      us:{' '}
      <a href="mailto:admin@getset4education.co.uk">
        admin@getset4education.co.uk
      </a>
    </p>
    <p className="LessonPrintFooter">
      {schoolName ? `${schoolName} - ` : ''}Copyright ©
      {new Date().getFullYear()} Get Set 4 Education
    </p>
  </>
);
